import React, { useCallback, useEffect, useState } from 'react';

interface PhotoCarouselProps {
  images: string[];
  intervalMs?: number;
}

const PhotoCarousel: React.FC<PhotoCarouselProps> = ({ images, intervalMs = 6500 }) => {
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const showRelative = useCallback(
    (offset: number) => {
      setIndex((prev) => (prev + offset + images.length) % images.length);
    },
    [images.length]
  );

  useEffect(() => {
    if (isPaused || images.length < 2) {
      return;
    }

    const interval = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % images.length);
    }, intervalMs);

    return () => window.clearInterval(interval);
  }, [isPaused, images.length, intervalMs, index]);

  if (images.length === 0) {
    return null;
  }

  return (
    <div
      className="photo-carousel"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
    >
      <div className="photo-carousel-slides">
        {images.map((src, slideIndex) => (
          <div
            key={src}
            className={`photo-carousel-slide${slideIndex === index ? ' is-active' : ''}`}
            aria-hidden={slideIndex !== index}
          >
            <img src={src} alt="" className="photo-carousel-img" />
          </div>
        ))}
      </div>

      <button
        type="button"
        className="photo-carousel-nav photo-carousel-prev"
        onClick={() => showRelative(-1)}
        aria-label="Previous photo"
      >
        ‹
      </button>
      <button
        type="button"
        className="photo-carousel-nav photo-carousel-next"
        onClick={() => showRelative(1)}
        aria-label="Next photo"
      >
        ›
      </button>

      <div className="photo-carousel-dots">
        {images.map((src, dotIndex) => (
          <button
            key={src}
            type="button"
            className={`photo-carousel-dot${dotIndex === index ? ' is-active' : ''}`}
            onClick={() => setIndex(dotIndex)}
            aria-label={`Show photo ${dotIndex + 1} of ${images.length}`}
            aria-current={dotIndex === index}
          />
        ))}
      </div>
    </div>
  );
};

export default PhotoCarousel;
